/**
 * Model for a snippet
 */
define([
  "backbone",
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, _, Messenger, Properties, SortableListModel) {
  "use strict";

  const SnippetModel = {};
  const kbSnippetsMap = {};

  SnippetModel.snippet = Backbone.Model.extend({
    defaults: {
      name: "",
      description: "",
      languageId: null,
      touchpointId: null,
      value: "",
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      let groupId = this.get("groupId") || 0;
      if (groupId < 0) {
        throw new Error("Invalid group ID");
      }
      const url = `${Properties.rootURL}/${kbId}/snippets/groups/${groupId}/snippets`;
      if (this.id) {
        // For some reason when call PUT version, backbone is not adding the ID in the end, so add
        // it manually
        return url + "/" + this.id;
      }
      return url;
    },
    move: function (groupModel) {
      const groupId = groupModel.id;
      const kbId = Messenger.reqres.request("active:kb");
      const moveUrl = `${Properties.rootURL}/${kbId}/snippets/groups/${groupId}/move`;
      this.set("groupId", groupId);
      return this.saveModelPromise({ url: moveUrl });
    },
    _validateDuplicateName: function (value) {
      if (this.collection) {
        for (let i = 0; i < this.collection.length; i++) {
          const model = this.collection.at(i);
          if (model !== this) {
            if (value.toUpperCase() === model.get("name").toUpperCase()) {
              return $.t("snippet.duplicateNameError");
            }
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "snippet.emptyNameError",
            maxLength: 100,
          }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateName(value)
        );
      }
      if (attribute === "value") {
        return this.validateRequiredValue(value, { emptyValueMessage: "snippet.emptyValueError" });
      }
    },
  });

  SnippetModel.snippets = Backbone.Collection.extend({
    model: SnippetModel.snippet,
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return `${Properties.rootURL}/${kbId}/snippets`;
    },
    addNewSnippet: function (groupId) {
      const newSnippet = new SnippetModel.snippet();
      newSnippet.set("groupId", groupId);
      this.add(newSnippet);
      return newSnippet;
    },
    translateSnippetsForResponse: function ({ responseString, showLabel }) {
      // Regex's to get content
      const snippetsRegex = new RegExp("<div data-snippet-id[^>]*>(.*?)</div>", "mg");
      const snippetIdRegex = /data-snippet-id="(\d+)"/;

      return responseString.replace(snippetsRegex, (snippetInResponse, snippetName) => {
        const snippetId = snippetInResponse.match(snippetIdRegex)[1];
        const snippet = this.get(snippetId);

        let replacement = this._getSnippetReplacementValue(snippet, snippetName);
        if (showLabel) {
          replacement = this._getSnippetLabel(snippetName) + replacement;
        }
        return replacement;
      });
    },
    _getSnippetLabel: function (snippetName) {
      return _.unescape(
        `<button class="btn btn-mini snippet-label btn-danger" title="${snippetName}" data-toggle="tooltip" data-i18n="topic.tooltip.snippetLabel"></button>`,
      );
    },
    _getSnippetReplacementValue: function (snippet) {
      if (!snippet) {
        return "";
      }
      return _.unescape(snippet.get("value"));
    },
  });

  SnippetModel.snippetGroup = Backbone.Model.extend({
    idAttribute: "groupId",
    defaults: {
      name: "",
      parentId: 0,
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return `${Properties.rootURL}/${kbId}/snippets/groups/${this.id}`;
    },
    isRoot: function () {
      return this.id === 0;
    },
    addSubGroup: function () {
      const newGroup = new SnippetModel.snippetGroup();
      newGroup.set("parentId", this.id);
      this.subGroups().add(newGroup);
      this.subGroups().sort();
      return newGroup;
    },
    subGroups: function () {
      const kbId = Messenger.reqres.request("active:kb");
      // using urlExtension was causing incorrect appending of url, so use the full url instead
      const subModelOptions = {
        doNotFetch: !this.get("hasSubgroups"),
        modelClass: SnippetModel.snippetGroups,
        shouldSort: true,
        url: `${Properties.rootURL}/${kbId}/snippets/groups/${this.id}/subgroups`,
      };
      this.subGroupsCol = this.getSubModel2(this.subGroupsCol, subModelOptions);
      this.subGroupsCol.parent = this;

      // add events to determine when to change the hasSubgroups attribute
      // if group has subfolders, set hasSubGroups to true (this may be sent as false from the server if the subgroup hasn't been saved yet)
      this.subGroupsCol.on("add remove reset sync", () => {
        const wasDirty = this.dirty;
        this.set("hasSubgroups", this.subGroupsCol.length > 0);
        this.dirty = wasDirty; //do not let the set event change the dirty flag because changing hasSubgroups should not require a save (but we do need to fire a change event)
      });
      return this.subGroupsCol;
    },
    snippets: function () {
      if (!this.snippetsCol) {
        //if the group exists on the server, load the entries from the server
        const kbId = Messenger.reqres.request("active:kb");
        // using urlExtension was causing incorrect appending of url, so use the full url instead
        const subModelOptions = {
          modelClass: SnippetModel.snippets,
          shouldSort: false,
          url: `${Properties.rootURL}/${kbId}/snippets/groups/${this.id}/snippets`,
          doNotFetch: this.isNew(),
        };
        //don't bother sorting snippets because the sortable list will deal with that for us
        this.snippetsCol = this.getSubModel2(this.snippetsCol, subModelOptions);
        this.snippetsCol.snippetGroup = this;
      }
      return this.snippetsCol;
    },
    move: function (snippetsToMove, destinationGroup) {
      if (this.snippetsCol) {
        snippetsToMove.forEach((snippet) => {
          // Move each snippet
          snippet
            .move(destinationGroup)
            .then(() => {
              // Remove snippet from the current collection
              this.snippetsCol.remove(snippet);
              // Add to destination collection if it exists
              if (destinationGroup.snippetsCol) {
                destinationGroup.snippetsCol.add(snippet);
              }
            })
            .catch((errorMsg) => {
              console.error("Could not move snippet", errorMsg);
              Messenger.okDialog(errorMsg);
            });
        });
      }
    },
    //move this folder to another folder. Assumes that this folder has already been removed from whatever
    //collection it was part of.
    moveFolder: function (destinationGroup) {
      this.set("parentId", destinationGroup.id);
      const destinationSubGroups = destinationGroup.subGroups();
      //when the destination group's subgroups are synced, add this group
      destinationSubGroups.onSynced(() => {
        destinationSubGroups.add(this);
        //make sure that there is no naming conflict in the new collection
        //add a (1),(2),(3)... to the end of the name if there is a conflict
        let value = this.get("name");
        const originalValue = value;
        let number = 1;
        while (this.validateAttribute("name", value)) {
          value = originalValue + "(" + number + ")";
          number++;
        }
        this.set("name", value, { silent: true });
        this.trigger("enter"); //trigger enter event as if the name change was made by a textbox
        destinationGroup.trigger("expand"); //make sure we see the moved folder in the parent folder
        this.trigger("select"); //make sure the current folder is still selected
      });
    },
    saveChildren: function (options) {
      //set the parentId of the children
      if (this.subGroupsCol) {
        const kbId = Messenger.reqres.request("active:kb");
        // Override the URL for each subgroup model for different operations
        this.subGroupsCol.each((subGroupModel) => {
          const subgroupUrl = `${Properties.rootURL}/${kbId}/snippets/groups/${this.id}/subgroups`;
          subGroupModel.url = subGroupModel.id
            ? `${subgroupUrl}/${subGroupModel.id}` // If it has an ID, use the full URL with ID
            : subgroupUrl; // If no ID, just use the base URL for creation
        });
      }
      this.saveSubModels(options);
    },
    _validateDuplicateGroupName: function (value) {
      if (this.collection) {
        for (let i = 0; i < this.collection.length; i++) {
          const model = this.collection.at(i);
          if (model !== this) {
            if (value.toUpperCase() === model.get("name").toUpperCase()) {
              return $.t("snippet.controls.duplicateValidationError");
            }
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, { maxLength: 100 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateGroupName(value)
        );
      }
    },
  });

  SnippetModel.snippetGroups = Backbone.Collection.extend({
    model: SnippetModel.snippetGroup,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
  });

  Messenger.reqres.setHandler("root:snippet:group", () => {
    const group = new SnippetModel.snippetGroup({ groupId: 0 });
    group.fetch();
    return group;
  });

  Messenger.reqres.setHandler("fetch:snippet", function (snippetId) {
    const snippet = new SnippetModel.snippet({ id: +snippetId });
    const kbId = Messenger.reqres.request("active:kb");
    snippet.url = `${Properties.rootURL}/${kbId}/snippets/${snippetId}`;
    snippet.fetch();
    return snippet;
  });

  Messenger.reqres.setHandler("kb:snippets", function (kbId) {
    const realKbId = kbId ? kbId : Messenger.reqres.request("active:kb");
    let snippets = kbSnippetsMap[realKbId];
    if (!snippets) {
      snippets = new SnippetModel.snippets();
      snippets.fetch();
      kbSnippetsMap[realKbId] = snippets;
    }
    return snippets;
  });

  Messenger.reqres.setHandler("snippet:list:headers", function () {
    const Header = SortableListModel.headerModel;
    const isAdmin = Messenger.reqres.request("user:is:admin");
    const headers = [];
    if (isAdmin) {
      headers.push(new Header({ checkbox: true }));
    }
    headers.push(
      new Header({
        headerName: $.t("topics.headers.name"),
        modelAttribute: "name",
        selectable: true,
        longTextField: true,
        sortable: true,
        filterable: true,
      }),
    );
    headers.push(
      new Header({
        headerName: $.t("admin.headers.description"),
        modelAttribute: "description",
        longTextField: true,
        sortable: true,
        filterable: true,
      }),
    );
    return new Backbone.Collection(headers);
  });

  return SnippetModel;
});
